import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "../i18n/i18n";
import useLocale from "../hooks/useLocale";
import { formatDateLocalized } from "../utils/date";
import StickyTitle from "../components/StickyTitle/StickyTitle";
import Loader from "../components/Loader/Loader";
import EditEntry from "../components/HistoryComponents/EditEntry";
import Modal from "../components/Modal/Modal";
import EmptyState from "../components/EmptyState/EmptyState";
import { db, auth } from "../firebase";
import {
	collection,
	query,
	where,
	orderBy,
	onSnapshot,
	updateDoc,
	deleteDoc,
	doc,
	Timestamp,
} from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import {
	ArrowRight,
	CalendarSync,
	Edit3,
	Trash2,
	Play,
	Pause,
	Utensils,
	Home,
	HeartPulse,
	User,
	Euro,
	Circle,
	FileText,
	AlertTriangle,
	CircleAlert,
	X,
} from "lucide-react";
import { toast } from "react-toastify";

const RecurringTransactions = () => {
	const { translate } = useTranslation();
	const { locale } = useLocale();
	const [recurringTransactions, setRecurringTransactions] = useState([]);
	const [loading, setLoading] = useState(true);
	const [editingTransaction, setEditingTransaction] = useState(null);
	const [showEditModal, setShowEditModal] = useState(false);
	const [isClosingEditModal, setIsClosingEditModal] = useState(false);
	const [togglingTransactions, setTogglingTransactions] = useState(new Set());
	const [showDeleteModal, setShowDeleteModal] = useState(false);
	const [transactionToDelete, setTransactionToDelete] = useState(null);
	const [error, setError] = useState(null);
	const navigate = useNavigate();

	// Memoize language derivation
	const language = useMemo(() => {
		return locale.startsWith("fr") ? "fr" : "en";
	}, [locale]);

	// Optimize: Memoize Firebase Functions instance and callable
	const { functions, toggleRecurringCloud } = useMemo(() => {
		const functionsInstance = getFunctions(undefined, "europe-west1");
		const toggleCallable = httpsCallable(
			functionsInstance,
			"toggleRecurringTransaction"
		);
		return {
			functions: functionsInstance,
			toggleRecurringCloud: toggleCallable,
		};
	}, []);

	// Memoize static category mappings
	const categories = useMemo(
		() => ({
			food: "category.food",
			general: "category.general",
			personal: "category.personal",
			health: "category.health",
			housing: "category.housing",
			income: "category.income",
		}),
		[]
	);

	// Memoize category icons
	const categoryIcons = useMemo(
		() => ({
			food: <Utensils size={16} />,
			housing: <Home size={16} />,
			health: <HeartPulse size={16} />,
			personal: <User size={16} />,
			income: <Euro size={16} />,
			general: <FileText size={16} />,
			other: <Circle size={16} />,
		}),
		[]
	);

	// Memoize currency formatter
	const formatCurrency = useMemo(() => {
		return (amount, lang) => {
			return new Intl.NumberFormat(lang === "fr" ? "fr-FR" : "en-GB", {
				style: "currency",
				currency: "EUR",
				minimumFractionDigits: 2,
			}).format(amount);
		};
	}, []);

	// Memoize date formatter
	const formatNextDate = useCallback(
		timestamp => {
			if (!timestamp) return "";
			const date = timestamp.toDate();
			return formatDateLocalized(date, locale);
		},
		[locale]
	);

	// Memoize frequency text generator
	const getFrequencyText = useCallback(
		(frequency, interval) => {
			const count = interval;
			const unit = frequency;

			// Use correct determinant
			const determinantKey =
				count === 1
					? `form.recurringDeterminant.${unit}.one`
					: `form.recurringDeterminant.${unit}.other`;
			const determinant = translate(determinantKey);

			// Use correct unit singular/plural
			const unitKey =
				count === 1
					? `form.recurring.${unit}`
					: `form.recurring.${unit}_plural`;
			const intervalAndUnit = translate(unitKey, { count });

			return `${determinant} ${intervalAndUnit}`;
		},
		[translate]
	);

	// Memoize sorted transactions to display active ones first
	const sortedRecurringTransactions = useMemo(() => {
		return recurringTransactions.sort((a, b) => {
			// Sort by isActive first (active transactions first)
			if (a.isActive && !b.isActive) return -1;
			if (!a.isActive && b.isActive) return 1;

			// If both have the same status, sort by next date (earliest first)
			const dateA = a.nextDate?.toDate?.() || new Date(0);
			const dateB = b.nextDate?.toDate?.() || new Date(0);
			return dateA.getTime() - dateB.getTime();
		});
	}, [recurringTransactions]);

	useEffect(() => {
		const userId = auth.currentUser?.uid;
		if (!userId) {
			setError(translate("recurring.userNotConnected"));
			setLoading(false);
			return;
		}

		const q = query(
			collection(db, "recurringTransactions"),
			where("userId", "==", userId),
			orderBy("createdAt", "desc")
		);

		const unsubscribe = onSnapshot(
			q,
			snapshot => {
				const transactions = [];
				snapshot.forEach(doc => {
					transactions.push({ id: doc.id, ...doc.data() });
				});
				setRecurringTransactions(transactions);
				setLoading(false);
			},
			err => {
				console.error("Error fetching recurring transactions:", err);
				setError(err.message);
				setLoading(false);
			}
		);

		return () => unsubscribe();
	}, [translate]);

	useEffect(() => {
		// Check if the currently editing transaction still exists
		if (editingTransaction && showEditModal && !isClosingEditModal) {
			const transactionStillExists = recurringTransactions.some(
				t => t.id === editingTransaction.id
			);

			// If the transaction was deleted, close the edit modal with animation
			if (!transactionStillExists) {
				handleCloseEditModal();
			}
		}
	}, [
		recurringTransactions,
		editingTransaction,
		showEditModal,
		isClosingEditModal,
	]);

	const handleEdit = useCallback(transaction => {
		// Convert the recurring transaction to a format compatible with EditEntry
		const editableTransaction = {
			id: transaction.id,
			title: transaction.title,
			amount: transaction.amount,
			category: transaction.category,
			subCategory: transaction.subCategory,
			type: transaction.type,
			// Always use nextDate as the editable date
			date: transaction.nextDate,
			// Add fields specific to recurrence
			frequency: transaction.frequency,
			interval: transaction.interval,
			isActive: transaction.isActive,
			// Flag to indicate this is a recurring transaction edit
			isRecurringEdit: true,
		};

		setEditingTransaction(editableTransaction);
		setShowEditModal(true);
		setIsClosingEditModal(false);
	}, []);

	const handleSaveEdit = useCallback(
		async updatedTransaction => {
			try {
				const docRef = doc(db, "recurringTransactions", updatedTransaction.id);

				// Get the new date from the form - this becomes the new nextDate directly
				const newNextDate = updatedTransaction.date.toDate
					? updatedTransaction.date.toDate()
					: new Date(updatedTransaction.date);

				// Ensure the date is set to start of day to avoid timezone issues
				newNextDate.setHours(0, 0, 0, 0);

				const updateData = {
					title: updatedTransaction.title,
					amount: parseFloat(updatedTransaction.amount),
					category: updatedTransaction.category,
					subCategory: updatedTransaction.subCategory || null,
					type: updatedTransaction.type,
					// The date from the form becomes the new nextDate directly
					nextDate: Timestamp.fromDate(newNextDate),
					frequency: updatedTransaction.frequency,
					interval: updatedTransaction.interval,
					isActive:
						updatedTransaction.isActive !== undefined
							? updatedTransaction.isActive
							: true,
					updatedAt: Timestamp.fromDate(new Date()),
				};

				await updateDoc(docRef, updateData);
				toast.success(translate("alert.recurringUpdateSuccess"));
				handleCloseEditModal();
			} catch (error) {
				console.error("Error updating recurring transaction:", error);
				toast.error(translate("alert.updateError"));
			}
		},
		[translate]
	);

	const handleToggleActive = useCallback(
		async transaction => {
			setTogglingTransactions(prev => new Set(prev).add(transaction.id));

			// Show loading toast and store the ID
			const loadingToastId = toast.loading(
				transaction.isActive
					? translate("recurring.pausing")
					: translate("recurring.resuming")
			);

			try {
				// Try cloud function toggle
				const result = await toggleRecurringCloud({
					recurringId: transaction.id,
					isActive: !transaction.isActive,
				});

				// Update loading toast to success
				const { isActive } = result.data;

				toast.update(loadingToastId, {
					render: isActive
						? translate("alert.recurringResumed")
						: translate("alert.recurringPaused"),
					type: "success",
					isLoading: false,
					autoClose: 3000,
				});
			} catch (error) {
				console.error("Error toggling recurring transaction:", error);

				// Try fallback method
				try {
					const docRef = doc(db, "recurringTransactions", transaction.id);
					await updateDoc(docRef, {
						isActive: !transaction.isActive,
						updatedAt: Timestamp.fromDate(new Date()),
					});

					toast.update(loadingToastId, {
						render: transaction.isActive
							? translate("alert.recurringPaused")
							: translate("alert.recurringResumed"),
						type: "success",
						isLoading: false,
						autoClose: 3000,
					});
				} catch (fallbackError) {
					console.error("Fallback method also failed:", fallbackError);
					toast.update(loadingToastId, {
						render: translate("alert.updateError"),
						type: "error",
						isLoading: false,
						autoClose: 3000,
					});
				}
			} finally {
				setTogglingTransactions(prev => {
					const newSet = new Set(prev);
					newSet.delete(transaction.id);
					return newSet;
				});
			}
		},
		[toggleRecurringCloud, translate]
	);

	const handleDelete = useCallback(transaction => {
		setTransactionToDelete(transaction);
		setShowDeleteModal(true);
	}, []);

	const confirmDelete = useCallback(async () => {
		if (!transactionToDelete) return;

		try {
			await deleteDoc(doc(db, "recurringTransactions", transactionToDelete.id));
			toast.success(translate("alert.recurringDeleted"));

			// If we're deleting the transaction that's currently being edited, close the edit modal
			if (
				editingTransaction &&
				editingTransaction.id === transactionToDelete.id
			) {
				handleCloseEditModal();
			}
		} catch (error) {
			console.error("Error deleting recurring transaction:", error);
			toast.error(translate("alert.deleteError"));
		} finally {
			setShowDeleteModal(false);
			setTransactionToDelete(null);
		}
	}, [transactionToDelete, editingTransaction, translate]);

	// Handler to close the edit modal with proper animation
	const handleCloseEditModal = useCallback(() => {
		if (isClosingEditModal) return; // Prevent multiple closures

		setIsClosingEditModal(true);
		setShowEditModal(false);

		// Clean up after animation completes
		setTimeout(() => {
			setEditingTransaction(null);
			setIsClosingEditModal(false);
		}, 300); // Match the CSS animation duration
	}, [isClosingEditModal]);

	// Handler to close the delete modal
	const handleCloseDeleteModal = useCallback(() => {
		setShowDeleteModal(false);
		setTransactionToDelete(null);
	}, []);

	// Handler to cancel editing from EditEntry
	const handleEditCancel = useCallback(() => {
		handleCloseEditModal();
	}, [handleCloseEditModal]);

	// Handler for when an entry is deleted from EditEntry
	const handleEntryDeleted = useCallback(
		entryId => {
			// If the deleted transaction is the one currently being edited, close the edit modal
			if (editingTransaction && editingTransaction.id === entryId) {
				handleCloseEditModal();
			}
		},
		[editingTransaction, handleCloseEditModal]
	);

	const handleNavigateToAdd = useCallback(() => {
		navigate("/add-transaction");
	}, [navigate]);

	if (error) {
		return (
			<div className="content">
				<StickyTitle title={translate("recurring.title")} />
				<div className="page-content">
					<EmptyState
						icon={CircleAlert}
						title={translate("error.title")}
						description={`${translate("error.message")} ${error}`}
						variant="error"
					/>
				</div>
			</div>
		);
	}

	if (loading) {
		return (
			<div className="content">
				<StickyTitle title={translate("recurring.title")} />
				<div className="page-content">
					<Loader
						fullscreen={true}
						loadingTextKey="recurring.loading"
						theme="light"
						translate={translate}
					/>
				</div>
			</div>
		);
	}

	return (
		<div className="content">
			<StickyTitle title={translate("recurring.title")} />

			{/* Confirm delete modal */}
			<Modal
				isOpen={showDeleteModal}
				onClose={handleCloseDeleteModal}
				animated={true}
				modalType="delete"
			>
				<div className="reauth-modal-content glass-effect">
					<div className="settings-title-container">
						<AlertTriangle size={20} color="#ff4444" />
						<h2>{translate("recurring.delete")}</h2>
					</div>
					<div className="layout-row design-form">
						<p>{translate("alert.confirmDeleteRecurring")}</p>
					</div>
					<div className="ios-buttons">
						<button className="ios-button" onClick={handleCloseDeleteModal}>
							<X size={18} />
							{translate("action.cancel")}
						</button>
						<button className="ios-button warning" onClick={confirmDelete}>
							<Trash2 size={18} />
							{translate("action.delete")}
						</button>
					</div>
				</div>
			</Modal>

			{/* Edit modal */}
			<Modal
				isOpen={showEditModal || isClosingEditModal}
				onClose={handleCloseEditModal}
				animated={true}
				modalType="edit"
			>
				<div className="edit-modal-content glass-effect">
					<div className="settings-title-container">
						<h1>{translate("recurring.edit")}</h1>
					</div>
					{editingTransaction && (
						<EditEntry
							entry={editingTransaction}
							editable={true}
							onSave={handleSaveEdit}
							onCancel={handleEditCancel}
							onEntryDeleted={handleEntryDeleted}
							isRecurring={true}
							isRecurringEdit={true}
						/>
					)}
				</div>
			</Modal>

			<div className="page-content">
				{recurringTransactions.length === 0 ? (
					<EmptyState
						icon={CalendarSync}
						title={translate("recurring.empty.title")}
						description={translate("recurring.empty.description")}
						actionButton={{
							text: translate("form.add"),
							onClick: handleNavigateToAdd,
							icon: ArrowRight,
						}}
					/>
				) : (
					<div className="recurring-list">
						{sortedRecurringTransactions.map(transaction => {
							const isToggling = togglingTransactions.has(transaction.id);

							return (
								<div
									key={transaction.id}
									className={`card glass-effect recurring-item ${
										!transaction.isActive ? "inactive" : ""
									}`}
								>
									<div className="recurring-header">
										{transaction.isActive ? (
											<span className="status active">
												{translate("recurring.active")}
											</span>
										) : (
											<span className="status inactive">
												{translate("recurring.paused")}
											</span>
										)}
										<div className="recurring-actions">
											<button
												className="ios-button action edit"
												onClick={() => handleEdit(transaction)}
												title={translate("action.edit")}
											>
												<Edit3 size={18} />
											</button>
											<button
												className={`ios-button ${
													transaction.isActive ? "pause" : "play"
												}`}
												onClick={() => handleToggleActive(transaction)}
												disabled={isToggling}
												title={
													transaction.isActive
														? translate("recurring.pause")
														: translate("recurring.resume")
												}
											>
												{transaction.isActive ? (
													<Pause size={18} />
												) : (
													<Play size={18} />
												)}
											</button>
											<button
												className="ios-button warning"
												onClick={() => handleDelete(transaction)}
												title={translate("action.delete")}
											>
												<Trash2 size={18} />
											</button>
										</div>
									</div>
									<div className="info-card">
										<div className="recurring-main">
											<span
												className={`category category-${transaction.category}`}
											>
												{categoryIcons[transaction.category] ||
													categoryIcons["other"]}
												{translate(categories[transaction.category])}
											</span>
											<h2 className="recurring-title">{transaction.title}</h2>
										</div>
										<div className="recurring-details">
											<span className={`amount ${transaction.type}`}>
												{transaction.type === "expense" ? "-" : "+"}
												{formatCurrency(Math.abs(transaction.amount), language)}
											</span>
											<span className="frequency">
												{getFrequencyText(
													transaction.frequency,
													transaction.interval
												)}
											</span>
											<span className="next-date">
												{translate("recurring.next")}{" "}
												{formatNextDate(transaction.nextDate)}
											</span>
										</div>
									</div>
								</div>
							);
						})}
					</div>
				)}
			</div>
		</div>
	);
};

export default RecurringTransactions;
